'use client';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PartResult {
  id: string;
  title: string;
  partNumber: string;
  price: string;
  condition: string;
  seller: {
    name: string;
    rating: number;
    feedbackCount: number;
    topRated: boolean;
  };
  shipping: string;
  imageUrl: string;
  imageAlt: string;
  compatibility: string;
  listingUrl: string;
}

interface PartsResultCardProps {
  part: PartResult;
}

const PartsResultCard = ({ part }: PartsResultCardProps) => {
  const handleViewListing = () => {
    window.open(part.listingUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm transition-smooth hover:shadow-elevation-md overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 p-4">
        <div className="relative h-48 md:h-full rounded-lg overflow-hidden bg-muted">
          <AppImage
            src={part.imageUrl}
            alt={part.imageAlt}
            className="w-full h-full object-cover"
          />
          {part.condition === 'New' && (
            <div className="absolute top-2 left-2 rounded-md bg-success px-2 py-1">
              <span className="font-caption text-xs font-medium text-success-foreground">
                New
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex-1">
            <h3 className="font-heading text-base font-semibold text-text-primary mb-2 line-clamp-2">
              {part.title}
            </h3>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="HashtagIcon" size={16} className="text-muted-foreground" />
                <span className="font-mono text-sm text-text-secondary">
                  {part.partNumber}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success" />
                <span className="font-body text-sm text-text-secondary">
                  {part.compatibility}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <Icon name="StarIcon" size={16} variant="solid" className="text-accent" />
                  <span className="font-body text-sm font-medium text-text-primary">
                    {part.seller.rating.toFixed(1)}
                  </span>
                </div>
                <span className="font-body text-sm text-text-secondary">
                  {part.seller.name}
                </span>
                {part.seller.topRated && (
                  <div className="flex items-center space-x-1 rounded-md bg-primary/10 px-2 py-0.5">
                    <Icon name="ShieldCheckIcon" size={14} className="text-primary" />
                    <span className="font-caption text-xs font-medium text-primary">
                      Top Rated
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Icon name="TruckIcon" size={16} className="text-muted-foreground" />
                <span className="font-body text-sm text-text-secondary">
                  {part.shipping}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <p className="font-caption text-xs text-text-secondary mb-1">Price</p>
              <p className="font-heading text-2xl font-bold text-primary">
                {part.price}
              </p>
            </div>

            <button
              onClick={handleViewListing}
              className="flex items-center space-x-2 rounded-lg bg-primary px-6 py-3 font-body text-sm font-medium text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97]"
            >
              <span>View on eBay</span>
              <Icon name="ArrowTopRightOnSquareIcon" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartsResultCard;